import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useContexts";

const PersistLogin = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { auth, setAuth, persist } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const safeParse = (value: string | null) => {
    try {
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  };

  const getTokenFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("token");
  };

  // ✅ Fetch full user details using the token, then login
  const handleTokenLogin = async (token: string) => {
    try {
      const res = await fetch(
        // ← replace with your endpoint
        `${import.meta.env.VITE_BACKEND_BASE_URL}/api/v1/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const json = await res.json();

      // Adjust this path based on your actual API response shape
      const user = json?.data?.user ?? json?.data ?? {};

      // ── Save to localStorage exactly like onFormSubmit does ──
      localStorage.setItem("token", JSON.stringify(token));
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("companyData", JSON.stringify(user));
      localStorage.setItem("persist", JSON.stringify(true));

      if (user?.company?.pcn_registration_status) {
        sessionStorage.setItem(
          "regStatus",
          user.company.pcn_registration_status,
        );
      }

      // ── Hydrate auth context ──
      setAuth({ token, user });
    } catch (err) {
      console.error("Token login failed:", err);
      // Token was invalid — fall through to localStorage or unauthenticated
    } finally {
      // ── Strip ?token from URL regardless of success/failure ──
      navigate(location.pathname, { replace: true });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        // URL token takes highest priority ──
        const tokenFromUrl = getTokenFromUrl();
        if (tokenFromUrl) {
          await handleTokenLogin(tokenFromUrl);
          return;
        }

        // Fallback to localStorage ──
        const storedToken = safeParse(localStorage.getItem("token"));
        const storedUser = safeParse(localStorage.getItem("user"));

        if (storedToken) {
          setAuth({ token: storedToken, user: storedUser });
        }
      } catch (err) {
        console.error("Auth init error:", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    if (!auth?.token) {
      initAuth();
    } else {
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.token]);

  return (
    <>
      {!persist ? <Outlet /> : isLoading ? <p>Loading page...</p> : <Outlet />}
    </>
  );
};

export default PersistLogin;
